import {
  Mail,
  Workflow,
  Sparkles,
  ShieldCheck,
  PenTool,
  BarChart3,
  Zap,
  LayoutGrid,
  GitBranch,
  Beaker,
  FlaskConical,
  Boxes,
  Gauge,
  Wand2,
  TerminalSquare,
  Smartphone,
  Inbox,
  LineChart,
  Bell,
  Layers,
  Users,
  Clock,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Agency", to: "/agency" },
  { label: "Apps", to: "/apps" },
  { label: "Lab", to: "/lab" },
  { label: "Work", to: "/work" },
];

export const PAGE_ACCENTS = {
  "/": "#00C4B0",
  "/agency": "#FF6B2B",
  "/apps": "#6FE7FF",
  "/lab": "#B7FF5A",
  "/work": "#FFC24B",
  "/contact": "#BFD7FF",
};

export const PAGE_ACCENTS_LIGHT = {
  "/": "#0BA697",
  "/agency": "#E8590C",
  "/apps": "#0E97BE",
  "/lab": "#5D9400",
  "/work": "#C9821A",
  "/contact": "#2F7DD1",
};

export const tint = (pct, color = "var(--page-accent)") =>
  `color-mix(in srgb, ${color} ${pct}%, transparent)`;

export const TICKER = [
  "RETENTION AGENCY",
  "PRODUCT APPS",
  "THE LAB",
  "EMAIL · SMS",
  "LIFECYCLE",
  "EXPERIMENTS",
  "DESIGN SYSTEMS",
  "OWNED REVENUE",
];

export const HOME_PILLARS = [
  {
    id: "agency",
    index: "01",
    title: "The Agency",
    to: "/agency",
    accent: "#FF6B2B",
    icon: Mail,
    blurb: "Full-service email & SMS retention. Strategy, copy, design, and sending — handled end to end.",
    tags: ["Email", "SMS", "Lifecycle"],
  },
  {
    id: "apps",
    index: "02",
    title: "The Apps",
    to: "/apps",
    accent: "#6FE7FF",
    icon: Smartphone,
    blurb: "Software for in-house teams who want to own the craft. Write, build, test, and measure.",
    tags: ["Product", "Tools", "Self-serve"],
  },
  {
    id: "lab",
    index: "03",
    title: "The Lab",
    to: "/lab",
    accent: "#B7FF5A",
    icon: FlaskConical,
    blurb: "Half-built ideas, on purpose. Prototypes, micro-tools, and experiments we ship to learn.",
    tags: ["Prototypes", "R&D", "Open"],
  },
];

export const HOME_STATS = [
  { value: "120+", label: "Brands scaled" },
  { value: "$24M+", label: "Revenue generated" },
  { value: "41%", label: "Avg. email share" },
  { value: "7", label: "Lab experiments live" },
];

export const AGENCY_SERVICES = [
  { icon: Mail, n: "01", title: "Email & SMS Management", blurb: "Strategy, copy, design and sending — your whole calendar, run for you." },
  { icon: Workflow, n: "02", title: "Flows & Automation", blurb: "Every lifecycle touchpoint built once and running while you sleep." },
  { icon: Sparkles, n: "03", title: "Lifecycle Strategy", blurb: "Segmentation, offers and cadence mapped to how people actually buy." },
  { icon: ShieldCheck, n: "04", title: "Deliverability", blurb: "Warming, authentication and list health so you land in the inbox." },
  { icon: PenTool, n: "05", title: "Design & Copy", blurb: "On-brand templates and words that sound like a human, not a coupon." },
  { icon: BarChart3, n: "06", title: "Reporting", blurb: "Clear weekly numbers tied to revenue, not vanity open rates." },
  { icon: Sparkles, n: "07", title: "Creative Strategy", blurb: "Planning, creating, media buying and analyzing results to get you better customers for cheaper." },
];

export const AGENCY_PROCESS = [
  { step: "01", title: "Audit", time: "Week 1", body: "We tear down your current program — flows, list health, deliverability and revenue share — and find the leaks." },
  { step: "02", title: "Strategy", time: "Week 2", body: "A 90-day retention roadmap: segments, offers, cadence and the flows that move revenue first." },
  { step: "03", title: "Build", time: "Weeks 3–6", body: "We design, write and ship the core flows and campaigns, instrumented so every send is measured." },
  { step: "04", title: "Iterate", time: "Ongoing", body: "Weekly testing and reporting. We double down on what works and quietly kill what doesn't." },
];

export const AGENCY_METRICS = [
  { value: "12", label: "Full funnels built" },
  { value: "7", label: "Services under one roof" },
  { value: "90D", label: "To a full system" },
  { value: "1", label: "Team. No handoffs." },
];

export const APP_FEATURES = [
  { icon: Wand2, title: "AI-assisted copy", blurb: "Draft on-brand subject lines and emails in seconds, then make them yours." },
  { icon: GitBranch, title: "Flow builder", blurb: "Drag, branch and test lifecycle automations on a visual canvas." },
  { icon: LineChart, title: "Revenue analytics", blurb: "See what each flow and campaign actually earns — not just opens." },
  { icon: Layers, title: "Template library", blurb: "Battle-tested, responsive templates you can brand in a click." },
  { icon: Gauge, title: "Deliverability monitor", blurb: "Inbox placement, spam signals and list health at a glance." },
  { icon: Bell, title: "Smart alerts", blurb: "Get pinged when a flow breaks or a number moves the wrong way." },
];

export const APP_PRODUCTS = [
  { name: "Emailink App", status: "Live", icon: Inbox, blurb: "The flagship retention workspace — copy, flows, analytics in one place.", accent: "#6FE7FF" },
  { name: "Flowforge", status: "Beta", icon: Workflow, blurb: "A standalone visual automation builder for lifecycle nerds.", accent: "#00C4B0" },
  { name: "Inboxlens", status: "Waitlist", icon: Gauge, blurb: "Deliverability and inbox-placement monitoring for senders at scale.", accent: "#FFC24B" },
];

export const APP_STATS = [
  { value: "4.2K", label: "Revenue / send" },
  { value: "44.2%", label: "Open rate" },
  { value: "7.3%", label: "Click rate" },
];

export const LAB_EXPERIMENTS = [
  { title: "Prompt Forge", tag: "AI", status: "Live", icon: TerminalSquare, blurb: "Select, compare, and apply strategic frameworks — MODELA, ASPECT, GREAT, and more — to analyze any business challenge." },
  { title: "Campaign Canvas", tag: "Tools", status: "Prototype", icon: LayoutGrid, blurb: "Track cold ad performance with a downloadable template and step-by-step guide. Drop your email, get the canvas." },
  { title: "Price Forge", tag: "Tools", status: "Prototype", icon: Gauge, blurb: "A pricing calculator for service businesses — start with sign shops, expand to anything. Quick quotes, no spreadsheets." },
  { title: "AstroAttraction", tag: "AI", status: "Live", icon: Sparkles, blurb: "A companion app for the Astro Attraction course — interactive lessons, trackers, and tools to apply what you learn." },
  { title: "Wonderland Method", tag: "Tools", status: "Live", icon: Smartphone, blurb: "The official companion app for the Wonderland Method book. Put the framework into practice, track your progress." },
  { title: "Miami Nights", tag: "Design", status: "Prototype", icon: Sparkles, blurb: "A nightlife experience prototype — neon-drenched, built for the after-hours crowd. Concept ready for a builder." },
  { title: "Quick Deck", tag: "Tools", status: "Prototype", icon: PenTool, blurb: "Generate polished client proposals in minutes. Pick a template, fill the blanks, export to PDF. No design skills needed." },
];

export const LAB_CHANGELOG = [
  { date: "2026.06", text: "Prompt Forge went live — select and compare strategic frameworks on the fly." },
  { date: "2026.06", text: "Lab rebuilt with 7 real projects from the pipeline. No more placeholders." },
];

export const WORK_CASES = [
  { brand: "Brandco", category: "DTC · Skincare", services: ["Email", "SMS", "Flows"], metric: "12% → 44%", metricLabel: "Email revenue share", blurb: "Rebuilt the entire lifecycle program in 90 days and tripled owned revenue.", accentFrom: "#FF6B2B", accentTo: "#FFC24B" },
  { brand: "Speed Hound", category: "Recovery Tech · Athletes", services: ["Email", "Strategy"], metric: "25% → 45%+", metricLabel: "Email revenue share", blurb: "Full email strategy rebuild — took them from 25% email revenue to 45%+ email attributed revenue.", accentFrom: "#00C4B0", accentTo: "#6FE7FF" },
  { brand: "Northwind", category: "Outdoor · Apparel", services: ["Strategy", "Design"], metric: "+318%", metricLabel: "Flow revenue", blurb: "A segmentation overhaul turned a flat list into a predictable channel.", accentFrom: "#00C4B0", accentTo: "#6FE7FF" },
  { brand: "Lumen", category: "Supplements", services: ["Email", "Deliverability"], metric: "99.1%", metricLabel: "Inbox placement", blurb: "Fixed a deliverability crisis and recovered six figures in lost sends.", accentFrom: "#6FE7FF", accentTo: "#BFD7FF" },
  { brand: "Maven & Co", category: "Home Goods", services: ["Flows", "Reporting"], metric: "4.1x", metricLabel: "ROI on retention", blurb: "Post-purchase and winback flows that paid for the whole program.", accentFrom: "#FFC24B", accentTo: "#FF6B2B" },
  { brand: "Halcyon", category: "Beverage", services: ["Email", "SMS"], metric: "+62%", metricLabel: "Repeat purchase rate", blurb: "A loyalty-led calendar that turned one-time buyers into regulars.", accentFrom: "#B7FF5A", accentTo: "#00C4B0" },
  { brand: "Atlas Goods", category: "Accessories", services: ["Strategy", "App"], metric: "$1.2M", metricLabel: "Added in 6 months", blurb: "Migrated the in-house team onto the Emailink app and scaled output 3x.", accentFrom: "#6FE7FF", accentTo: "#B7FF5A" },
];

export const TESTIMONIALS = [
  {
    quote:
      "Working with Emailink felt like someone finally understood what our list was actually worth. Email went from 12% to 44% of revenue in four months.",
    name: "A. Harmon",
    role: "Founder, Brandco",
    big: true,
  },
  { quote: "They treat retention like a product, not a campaign calendar.", name: "J. Okafor", role: "CMO, Northwind" },
  { quote: "The app gave our in-house team superpowers. We ship twice as fast.", name: "R. Vega", role: "Lifecycle Lead, Atlas" },
  { quote: "Finally an agency that shows its numbers and kills what doesn't work.", name: "S. Lin", role: "VP Growth, Lumen" },
];

export const CONTACT_FAQS = [
  { q: "Who do you work with?", a: "Mostly DTC and ecommerce brands doing $1M–$50M who know their list is underperforming. If retention is the bottleneck, we should talk." },
  { q: "Agency or app — which is right for me?", a: "If you want it handled, that's the agency. If you have an in-house team that wants better tools, that's the app. Plenty of brands use both." },
  { q: "How fast can we start?", a: "Audits typically kick off within a week of our first call. A full 90-day system is live inside the first quarter." },
  { q: "What's the Lab?", a: "It's where we ship experiments and micro-tools to learn in public. Some graduate into the app. Some are just fun." },
  { q: "Do you offer one-off projects?", a: "Occasionally — usually audits or flow builds. Most of our work is an ongoing partnership, because retention compounds." },
];

export const STATUS_COLORS = {
  Live: "#6FE7FF",
  Beta: "#B7FF5A",
  Waitlist: "#FFC24B",
  New: "#00C4B0",
};

export const APP_INCLUDES = [
  { icon: Gauge, label: "Deliverability-aware" },
  { icon: Wand2, label: "AI-assisted" },
  { icon: LineChart, label: "Revenue analytics" },
  { icon: Layers, label: "One-click templates" },
];

export const APPS_FEATURED = [
  {
    name: "StrategyMap",
    category: "Command Center",
    status: "Live",
    accent: "#FFC24B",
    variant: "analytics",
    icon: TerminalSquare,
    desc: "A war-room command center for your life — plan, execute, and track your strategies like a general.",
    points: ["Strategic planning canvas", "Mission-based daily execution", "Performance analytics dashboard"],
  },
  {
    name: "MSS Operations",
    category: "Operations",
    status: "Live",
    accent: "#f97316",
    variant: "flow",
    icon: Workflow,
    desc: "Complete operations platform for custom sign shops — replaces QuickBooks, spreadsheets, and whiteboards.",
    points: ["Estimating & quoting pipeline", "Site surveys, permits & approvals", "Production queue & dispatch"],
  },
  {
    name: "Afterglow",
    category: "Mobile",
    status: "Live",
    accent: "#D946EF",
    variant: "report",
    icon: Sparkles,
    desc: "The private journal for your rave life. Log the fits, the crew, the sets, and the feeling. Your camera roll forgets. This remembers.",
    points: ["Event journal with photos & vibe ratings", "Crew tags and outfit tracking", "Personal timeline of every night out"],
  },
];

export const APPS_CATALOG = [
  { name: "Subjectlab", category: "Copy", status: "Live", accent: "#6FE7FF", variant: "editor", icon: Wand2, desc: "Generate and score 50 subject lines, ranked by predicted open rate." },
  { name: "Segment Studio", category: "Audience", status: "Live", accent: "#B7FF5A", variant: "segments", icon: Users, desc: "Build, preview and sync high-intent segments without touching SQL." },
  { name: "Template Vault", category: "Design", status: "Live", accent: "#6FE7FF", variant: "templates", icon: Layers, desc: "A library of responsive, on-brand templates you can ship in a click." },
  { name: "Sendtime AI", category: "Optimization", status: "Beta", accent: "#00C4B0", variant: "calendar", icon: Clock, desc: "Predicts the exact hour each subscriber is most likely to open." },
  { name: "Revenue Radar", category: "Analytics", status: "Live", accent: "#6FE7FF", variant: "analytics", icon: LineChart, desc: "See what every campaign and flow actually earns \u2014 not just opens." },
  { name: "Pulse Alerts", category: "Monitoring", status: "Beta", accent: "#FFC24B", variant: "alerts", icon: Bell, desc: "Get pinged the moment a flow breaks or a number moves the wrong way." },
  { name: "Copyweld", category: "Copy", status: "Beta", accent: "#00C4B0", variant: "editor", icon: PenTool, desc: "An AI writing partner trained on your brand voice and best performers." },
  { name: "Warmup", category: "Deliverability", status: "Waitlist", accent: "#FFC24B", variant: "gauge", icon: ShieldCheck, desc: "Automated inbox warming that ramps new domains the safe way." },
  { name: "Snapshot", category: "Reporting", status: "New", accent: "#B7FF5A", variant: "report", icon: BarChart3, desc: "Beautiful weekly reports your clients and CFO will actually read." },
];

export const BUDGET_OPTIONS = [
  "Under $5k / mo",
  "$5k – $10k / mo",
  "$10k – $25k / mo",
  "$25k+ / mo",
  "Just exploring",
];
